import GroupIcon from "@mui/icons-material/Group";
import SettingsIcon from "@mui/icons-material/Settings";
import Badge from "@mui/material/Badge";
import FormControl from "@mui/material/FormControl";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Typography from "@mui/material/Typography";
import cx from "classnames";
import React, { useState, forwardRef } from "react";
import {
	AppBar,
	Logout,
	MenuItemLink,
	UserMenu,
	useGetIdentity,
	useStore,
	useTranslate,
	useUserMenu,
	useRedirect
} from "react-admin";
import styles from "./AppBar.module.scss";
import authProvider from "./authProvider";
import { ExportIcon } from "./exports";
import logo from "./images/logo-responsive.png";
import logoLegiwatch from "./images/legiwatch_light.png";

const AccountMenu = forwardRef((props, ref) => {
	const { onClose } = useUserMenu();
	const translate = useTranslate();

	return (
		<MenuItemLink
			ref={ref}
			to="/account"
			primaryText={translate("pos.account.section_title")}
			leftIcon={<SettingsIcon />}
			onClick={onClose}
			className={styles.navbarButton}
		/>
	);
});

const MembersMenu = forwardRef((props, ref) => {
	const { onClose } = useUserMenu();
	const translate = useTranslate();

	return (
		<MenuItemLink
			ref={ref}
			to="/team"
			primaryText={translate("resources.application_members.members")}
			leftIcon={<GroupIcon />}
			onClick={onClose}
			className={styles.navbarButton}
		/>
	);
});

const ExportsMenu = forwardRef((props, ref) => {
	const { onClose } = useUserMenu();
	const [exports] = useStore("exports");
	const translate = useTranslate();

	return (
		<MenuItemLink
			ref={ref}
			to="/exports"
			primaryText={
				<>
					{translate("resources.exports.name", { smart_count: 2 })}
					<Badge
						badgeContent={exports?.length}
						color="primary"
						sx={{ marginLeft: "15px" }}
					/>
				</>
			}
			leftIcon={<ExportIcon />}
			onClick={onClose}
			className={styles.navbarButton}
		/>
	);
});

const MyUserMenu = (props) => {
	return (
		<UserMenu {...props}>
			<ExportsMenu />
			<MembersMenu />
			<AccountMenu />
			<Logout />
		</UserMenu>
	);
};

const isParliamentHost = () => {
	const hostname = window.location.hostname;
	if (hostname.includes("legiwatch.fr") || process.env.REACT_APP_THEME === "parliament") {
		return true;
	}
	return false;
};

const MyAppBar = (props) => {
	const { data: identity, isLoading: identityLoading } = useGetIdentity();
	const [currentApplication, setCurrentApplication] = useState(
		localStorage.getItem("currentApplication"),
	);
	const redirectTo = useRedirect();


	const onApplicationChange = (e) => {
		setCurrentApplication(e.target.value);
		localStorage.setItem("currentApplication", e.target.value);
		authProvider
			.getIdentity()
			.then(() => {
				window.location.reload();
			})
			.catch(() => {
				window.location.reload();
			});
	};

	return (
		<AppBar
			{...props}
			className={styles.appBar}
			userMenu={<MyUserMenu />}
			elevation={1}
			alwaysOn
		>
			<img
				src={isParliamentHost() ? logoLegiwatch : logo}
				height={35}
				alt="Logo"
				style={{ paddingBottom: "5px", cursor: "pointer" }}
				onClick={() => {
					redirectTo("/");
				}}
			/>
			{!identityLoading && identity?.admin_applications?.length && !isParliamentHost() ? (
				<FormControl variant="outlined" className={styles.formControl}>
					<Select
						id="role_select"
						value={currentApplication || identity.admin_applications[0].name}
						onChange={(e) => onApplicationChange(e)}
						fullWidth
						className={cx({
							[styles.selectDisabled]: identity.admin_applications.length <= 1,
							[styles.select]: true,
						})}
						disabled={identity.admin_applications.length <= 1}
					>
						{identity.admin_applications.map((app) => (
							<MenuItem value={app.name} key={app.name}>
								{app.display_name || app.name}
							</MenuItem>
						))}
					</Select>
				</FormControl>
			) : null}
			<Typography
				variant="h6"
				color="inherit"
				className={styles.title}
				id="react-admin-title"
			/>
			<span className={styles.spacer} />
		</AppBar>
	);
};

export default MyAppBar;
